// Shared constants between client and server
// D8 (MASTER_PLAN §5): only genuinely translated locales ship. The other ten
// (nl-BE, fr, de, de-CH, da, no, sv, it, es, pt) remain scaffolded in
// public/locales/ but are English placeholders — re-add a locale here and in
// the language pickers only once its translation is real.
export const supportedLngs = ['en', 'nl'] as const
export type SupportedLanguage = (typeof supportedLngs)[number]

export const fallbackLng = 'nl' as const
export const defaultNS = 'common' as const

// All available namespaces — must match the files in public/locales/<lng>/
export const allNamespaces = ['common', 'marketing', 'app', 'auth', 'errors'] as const
export type Namespace = (typeof allNamespaces)[number]

// Critical namespaces that should be loaded on every page
export const criticalNamespaces = ['common', 'errors'] as const

// Page-specific namespace mapping
export const routeNamespaces: Record<string, readonly Namespace[]> = {
	'/': ['marketing', 'common'],
	'/login': ['auth', 'common', 'errors'],
	'/signup': ['auth', 'common', 'errors'],
	'/forgot-password': ['auth', 'common', 'errors'],
	'/reset-password': ['auth', 'common', 'errors'],
	'/onboarding': ['auth', 'common'],
	'/dashboard': ['app', 'common'],
	'/quotes': ['app', 'common'],
	'/templates': ['app', 'common'],
	'/contacts': ['app', 'common'],
	'/organizations': ['app', 'common'],
	'/deals': ['app', 'common'],
	'/activities': ['app', 'common'],
	// Settings pages read from the 'app' namespace (t('app:profile.*')) —
	// a 'settings' namespace file has never existed in public/locales
	'/settings': ['app', 'common', 'auth'],
	'/about': ['marketing', 'common'],
	'/pricing': ['marketing', 'common'],
	'/prijzen': ['marketing', 'common'],
	'/faq': ['marketing', 'common'],
	'/help': ['app', 'common'],
} as const

// Get namespaces for a specific route
export function getNamespacesForRoute(pathname: string): readonly Namespace[] {
	// Check exact match first
	if (routeNamespaces[pathname]) {
		return routeNamespaces[pathname]
	}

	// Check for pattern matches (e.g., /quotes/* matches /quotes)
	const pathSegments = pathname.split('/').filter(Boolean)
	if (pathSegments.length > 0) {
		// Try progressively shorter paths
		for (let i = pathSegments.length; i > 0; i--) {
			const basePath = '/' + pathSegments.slice(0, i).join('/')
			if (routeNamespaces[basePath]) {
				return routeNamespaces[basePath]
			}
		}
	}

	// Default namespaces for unknown routes
	return ['common', 'errors']
}

// Type-safe language validation
export function isSupportedLanguage(lng: string): lng is SupportedLanguage {
	return supportedLngs.includes(lng as SupportedLanguage)
}

// Get the closest supported language from a locale string
export function getClosestSupportedLanguage(locale: string): SupportedLanguage {
	// First try exact match
	if (isSupportedLanguage(locale)) {
		return locale
	}

	// Then try language part only (e.g., 'en-US' -> 'en', 'nl-BE' -> 'nl')
	const langPart = locale.split('-')[0]
	if (langPart && isSupportedLanguage(langPart)) {
		return langPart
	}

	return fallbackLng
}

// Get the best matching language from Accept-Language header
export function getBestLanguageMatch(acceptLanguage: string | null): SupportedLanguage {
	if (!acceptLanguage) return fallbackLng

	// Parse Accept-Language header
	const languages = acceptLanguage
		.split(',')
		.map(lang => {
			const [code, qValue] = lang.trim().split(';q=')
			return {
				code: code?.toLowerCase() || '',
				quality: qValue ? parseFloat(qValue) : 1
			}
		})
		.filter(lang => lang.code !== '')
		.sort((a, b) => b.quality - a.quality)

	// Find best match
	for (const { code } of languages) {
		// Check exact match
		if (isSupportedLanguage(code)) {
			return code
		}

		// Check language without region (e.g., 'en' from 'en-US')
		const baseLanguage = code.split('-')[0]
		if (baseLanguage && isSupportedLanguage(baseLanguage)) {
			return baseLanguage
		}

		// Check if we have a regional variant (e.g., 'nl-BE' when 'nl' is requested)
		if (baseLanguage) {
			const regionalVariant = supportedLngs.find(lng => lng.startsWith(baseLanguage + '-'))
			if (regionalVariant) {
				return regionalVariant
			}
		}
	}

	return fallbackLng
}
